import '../../styles/_faculty-panel.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { useUpsertFacultyRatingMutation } from '../../datasource/mutations/faculty';
import facultySlice, { useGetFacultyRatingByProgramSkQuery } from '../../datasource/queries/faculty';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useFaculty } from '../../hooks/useFaculty';
import { clsx, getInitialsFromName } from '../../utility/utils';
import { Rating } from '../Rating/Rating';
import { RatingLoading } from '../Rating/RatingLoading';
import { setSelectedFaculty } from '../Store/facultySlice';
import { addAnnouncement, removeToast } from '../Store/toastSlice';

const RATINGS = [
    { value: 1, label: 'Poor' },
    { value: 2, label: 'Fair' },
    { value: 3, label: 'Good' },
    { value: 4, label: 'Very Good' },
    { value: 5, label: 'Excellent' },
];

export const FacultyDetail = () => {
    const { faculty } = useFaculty();
    const selectedFaculty = useAppSelector((state) => state.faculty.selectedFaculty);
    const programSk = useAppSelector((state) => state.player.programSk);
    const forceFacultyDetails = (faculty?.length ?? 0) <= 1;
    const dispatch = useAppDispatch();
    const clientConfig = useAppSelector((state) => state.clientConfig);

    const [imageError, setImageError] = useState(false);

    const { isFetching: isFetchingRating, data: rating } = useGetFacultyRatingByProgramSkQuery({
        programSk,
        facultySk: selectedFaculty?.individualSk ?? 0,
    }, {
        skip: !selectedFaculty?.individualSk || !programSk,
    });

    const [upsertFacultyRating] = useUpsertFacultyRatingMutation();
    const debouncedUpsertFacultyRating = useDebounceCallback(upsertFacultyRating, 1000);

    async function handleRatingChange($value: string | number) {
        const value = Number($value);

        const previousRating = rating?.rating;

        const facultySk = selectedFaculty?.individualSk ?? 0;

        // optimistic update
        dispatch(facultySlice.util.updateQueryData('getFacultyRatingByProgramSk',
            { programSk, facultySk },
            draft => ({
                ...draft,
                rating: value,
            })
        ));

        try {
            const response = await debouncedUpsertFacultyRating({ programSk, facultySk, rating: value })?.unwrap();

            if (response && !response.isSuccess)
                throw new Error('Failed to submit rating');
        } catch (error) {
            if (error instanceof Error)
                console.error('Failed to submit rating', error);

            const id = Date.now();
            dispatch(addAnnouncement({
                id,
                contentType: 'text',
                message: 'There was an issue submitting your rating. Please try again.',
                expirationTimeoutId: window.setTimeout(() => dispatch(removeToast(id)), 5000),
            }));
            // revert optimistic update
            dispatch(facultySlice.util.updateQueryData('getFacultyRatingByProgramSk',
                { programSk, facultySk },
                draft => ({
                    ...draft,
                    rating: previousRating,
                })
            ));
        }
    }

    useEffect(() => {
        if (forceFacultyDetails && faculty) {
            dispatch(setSelectedFaculty({
                individualSk: faculty[0].individualSk,
                name: faculty[0].displayName ?? '',
                institutionName: faculty[0].institutionName ?? '',
                biography: faculty[0].biography ?? '',
                imageUrl: `${clientConfig?.baseContentUrl}/api/client/faculty-image-map/${faculty[0].individualSk}`
            }));
        }
    }, [clientConfig, dispatch, faculty, forceFacultyDetails]);

    const initials = useMemo(() => getInitialsFromName(selectedFaculty?.name), [selectedFaculty?.name]);
    const firstName = useMemo(() => selectedFaculty?.name.split(' ').shift() ?? '', [selectedFaculty?.name]);

    return (
        <div className='faculty-detail'>
            {
                !imageError ?
                    <img src={selectedFaculty?.imageUrl} alt={selectedFaculty?.name} onError={() => setImageError(true)} />
                    :
                    <div className='faculty-detail__image-fallback'>
                        {initials}
                    </div>
            }
            <div>
                <div className='faculty-detail__title heading-6--medium'>{selectedFaculty?.name}</div>
                <div className='paragraph-2 text--medium-grey'>{selectedFaculty?.institutionName}</div>
            </div>
            <div>
                <div className={clsx('faculty-detail__rating-title paragraph-1--medium', isFetchingRating && 'shimmer')}>Let us know what you think of {firstName}’s presentation</div>
                {isFetchingRating
                    ? <RatingLoading items={RATINGS.length} />
                    : (
                        <Rating
                            name="faculty-rating"
                            ratings={RATINGS}
                            value={rating?.rating ?? null}
                            onChange={handleRatingChange}
                            controlled
                        />
                    )}
            </div>
            {
                selectedFaculty?.biography &&
                <div>
                    <div className='paragraph-1 faculty-detail__title'>Biography</div>
                    <div className='paragraph-2 text--medium-grey' dangerouslySetInnerHTML={{ __html: selectedFaculty?.biography ?? '' }}></div>
                </div>
            }
        </div>
    );
};

export default FacultyDetail;
